import { useCallback, useMemo } from "react";
import type { ApplicationFilterOptionRestriction } from "./IApplicationFilterComponent";
import {
    buildRestrictionValueSet,
    isValueAllowedByOptionRestriction,
    resolveOptionRestriction,
} from "./filterRestrictions";

type OptionRestrictionInput = {
    optionRestriction?: ApplicationFilterOptionRestriction;
    restrictedValues?: unknown[];
};

type UseRestrictedOptionsArgs<TOption> = OptionRestrictionInput & {
    options?: TOption[] | null;
    getOptionValue: (option: TOption) => unknown;
    excludeEmptyValues?: boolean;
};

const EMPTY_OPTIONS: never[] = [];

export function useOptionRestriction({
    optionRestriction: optionRestrictionProp,
    restrictedValues: restrictedValuesProp,
}: OptionRestrictionInput) {
    const optionRestriction = useMemo(() => {
        return resolveOptionRestriction(
            optionRestrictionProp,
            restrictedValuesProp,
        );
    }, [optionRestrictionProp, restrictedValuesProp]);

    const restrictedValueSet = useMemo(
        () => buildRestrictionValueSet(optionRestriction),
        [optionRestriction],
    );

    const hasRestrictions = restrictedValueSet.size > 0;

    const isValueAllowed = useCallback(
        (value: unknown) => {
            return isValueAllowedByOptionRestriction(
                value,
                optionRestriction,
                restrictedValueSet,
            );
        },
        [optionRestriction, restrictedValueSet],
    );

    return {
        optionRestriction,
        restrictedValueSet,
        hasRestrictions,
        isValueAllowed,
    };
}

export function useRestrictedOptions<TOption>({
    options,
    getOptionValue,
    excludeEmptyValues = false,
    optionRestriction,
    restrictedValues,
}: UseRestrictedOptionsArgs<TOption>) {
    const {
        optionRestriction: resolvedOptionRestriction,
        restrictedValueSet,
        hasRestrictions,
        isValueAllowed,
    } = useOptionRestriction({
        optionRestriction,
        restrictedValues,
    });

    const availableOptions = useMemo(() => {
        const normalizedOptions = options ?? (EMPTY_OPTIONS as TOption[]);
        if (!hasRestrictions) return normalizedOptions;

        return normalizedOptions.filter((option) => {
            const optionValue = getOptionValue(option);
            if (excludeEmptyValues && `${optionValue ?? ""}` === "") {
                return false;
            }

            return isValueAllowed(optionValue);
        });
    }, [
        excludeEmptyValues,
        getOptionValue,
        hasRestrictions,
        isValueAllowed,
        options,
    ]);

    return {
        optionRestriction: resolvedOptionRestriction,
        restrictedValueSet,
        hasRestrictions,
        isValueAllowed,
        availableOptions,
    };
}
